import { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const MOODS = [
    { name: 'Grateful', emoji: '🙏', color: 'from-amber-50 to-amber-100 border-amber-200' },
    { name: 'Blessed', emoji: '✨', color: 'from-purple-50 to-purple-100 border-purple-200' },
    { name: 'Hopeful', emoji: '🌅', color: 'from-orange-50 to-orange-100 border-orange-200' },
    { name: 'Joyful', emoji: '😊', color: 'from-yellow-50 to-yellow-100 border-yellow-200' },
    { name: 'Peaceful', emoji: '🕊️', color: 'from-blue-50 to-blue-100 border-blue-200' },
    { name: 'Seeking', emoji: '🔍', color: 'from-indigo-50 to-indigo-100 border-indigo-200' },
    { name: 'Struggling', emoji: '💪', color: 'from-rose-50 to-rose-100 border-rose-200' },
    { name: 'Anxious', emoji: '😰', color: 'from-red-50 to-red-100 border-red-200' },
    { name: 'Weary', emoji: '😔', color: 'from-gray-50 to-gray-100 border-gray-200' },
];

export const MoodTracker = ({ onMoodSelect, selectedMood }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [pendingMood, setPendingMood] = useState(null);
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [mindsetShift, setMindsetShift] = useState(null);
    const [loadingMindset, setLoadingMindset] = useState(false);

    const handleMoodClick = (mood) => {
        setPendingMood(mood);
        setNote('');
        setMindsetShift(null);
        setIsDialogOpen(true);
    };

    const saveMood = async () => {
        if (!pendingMood) return;
        
        setSaving(true);
        try {
            await axios.post(`${API_URL}/moods`, {
                mood: pendingMood.name,
                note: note || null
            });
            
            toast.success('Mood logged! God bless your day.');
            onMoodSelect?.(pendingMood.name);
            setIsDialogOpen(false);
        } catch (error) {
            toast.error('Failed to save mood. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const getMindsetShift = async () => {
        if (!pendingMood) return;
        
        setLoadingMindset(true);
        try {
            const response = await axios.post(`${API_URL}/inspiration/mindset-shift?mood=${pendingMood.name}`);
            setMindsetShift(response.data.mindset_shift);
        } catch (error) {
            toast.error('Failed to generate mindset shift.');
        } finally {
            setLoadingMindset(false);
        }
    };

    return (
        <>
            <div className="mood-grid" data-testid="mood-tracker">
                {MOODS.map((mood) => (
                    <button
                        key={mood.name}
                        onClick={() => handleMoodClick(mood)}
                        className={`mood-card bg-gradient-to-br ${mood.color} ${
                            selectedMood === mood.name ? 'selected ring-2 ring-primary' : ''
                        }`}
                        data-testid={`mood-${mood.name.toLowerCase()}`}
                    >
                        <span className="text-2xl md:text-3xl">{mood.emoji}</span>
                        <span className="text-xs font-medium text-[#2D2A26]">{mood.name}</span>
                    </button>
                ))}
            </div>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3 font-display text-xl">
                            <span className="text-3xl">{pendingMood?.emoji}</span>
                            You're feeling {pendingMood?.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-6 mt-4">
                        <div>
                            <label className="text-sm font-medium text-muted-foreground mb-2 block">
                                Add a note (optional)
                            </label>
                            <Textarea
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                placeholder="What's on your heart today?"
                                rows={3}
                                className="resize-none"
                                data-testid="mood-note-input"
                            />
                        </div>

                        {/* Mindset Shift Section */}
                        {(pendingMood?.name === 'Struggling' || 
                          pendingMood?.name === 'Anxious' || 
                          pendingMood?.name === 'Weary') && (
                            <div className="bg-primary/5 rounded-2xl p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <Sparkles className="w-4 h-4 text-primary" />
                                        <span className="text-sm font-medium">Mindset Shift</span>
                                    </div>
                                    {!mindsetShift && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={getMindsetShift}
                                            disabled={loadingMindset}
                                            data-testid="get-mindset-shift-btn"
                                        >
                                            {loadingMindset ? 'Loading...' : 'Get Encouragement'}
                                        </Button>
                                    )}
                                </div>
                                {mindsetShift && (
                                    <p className="text-sm text-[#2D2A26] leading-relaxed" data-testid="mindset-shift-text">
                                        {mindsetShift}
                                    </p>
                                )}
                                {!mindsetShift && !loadingMindset && (
                                    <p className="text-sm text-muted-foreground">
                                        God sees you and loves you. Would you like an encouraging word?
                                    </p>
                                )}
                            </div>
                        )}

                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="ghost"
                                onClick={() => setIsDialogOpen(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={saveMood}
                                disabled={saving}
                                className="btn-primary"
                                data-testid="save-mood-btn"
                            >
                                {saving ? 'Saving...' : 'Save Mood'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MoodTracker;
